import { MessageCircle, X, ChevronRight } from 'lucide-react';
import { useDM } from '@/context/DMContext';
import { formatTimestamp } from '@/utils/formatters';
import { UserAvatar } from '@/components/ui/UserAvatar';
import type { DMContact } from '@/types/dm';

interface DMInboxProps {
  onClose: () => void;
  onSelectConversation: (conversationId: string) => void;
  onViewAll: () => void;
}

export function DMInbox({ onClose, onSelectConversation, onViewAll }: DMInboxProps) {
  const { conversations, totalUnreadCount } = useDM();

  // Show only recent conversations with unread first
  const sortedConversations = [...conversations]
    .sort((a, b) => {
      // Unread first
      if (a.metadata.unreadCount > 0 && b.metadata.unreadCount === 0) return -1;
      if (a.metadata.unreadCount === 0 && b.metadata.unreadCount > 0) return 1;
      // Then by last message time
      const aTime = a.conversation.lastMessageAt?.toMillis() || 0;
      const bTime = b.conversation.lastMessageAt?.toMillis() || 0;
      return bTime - aTime;
    })
    .slice(0, 5);

  const handleSelect = (conversationId: string) => {
    onSelectConversation(conversationId);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[90]" onClick={onClose} />

      {/* Dropdown - Fixed positioned and centered */}
      <div className="fixed left-1/2 -translate-x-1/2 top-[calc(var(--header-height)+var(--safe-area-top)+8px)] w-[calc(100%-2rem)] max-w-sm bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-[100]">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2">
            <MessageCircle size={18} className="text-[#8e4585]" />
            <h3 className="font-medium text-slate-800">Messages</h3>
            {totalUnreadCount > 0 && (
              <span className="px-2 py-0.5 bg-[#8e4585] text-white text-xs font-bold rounded-full">
                {totalUnreadCount}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded">
            <X size={16} />
          </button>
        </div>

        {/* Conversation list */}
        <div className="max-h-80 overflow-y-auto">
          {sortedConversations.length === 0 ? (
            <div className="p-6 text-center text-slate-400">
              <MessageCircle size={32} strokeWidth={1} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No messages yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {sortedConversations.map((contact) => (
                <InboxItem
                  key={contact.conversation.id}
                  contact={contact}
                  onClick={() => handleSelect(contact.conversation.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {conversations.length > 0 && (
          <button
            onClick={() => {
              onViewAll();
              onClose();
            }}
            className="w-full p-3 border-t border-slate-100 text-sm text-[#8e4585] font-medium hover:bg-slate-50 flex items-center justify-center gap-1"
          >
            View all messages
            <ChevronRight size={16} />
          </button>
        )}
      </div>
    </>
  );
}

interface InboxItemProps {
  contact: DMContact;
  onClick: () => void;
}

function InboxItem({ contact, onClick }: InboxItemProps) {
  const { conversation, metadata, otherUser } = contact;
  const hasUnread = metadata.unreadCount > 0;

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors text-left ${
        hasUnread ? 'bg-[#f8e8f8]/30' : ''
      }`}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <UserAvatar photoURL={otherUser.photoURL} displayName={otherUser.displayName} size={40} />

        {/* Unread indicator */}
        {hasUnread && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#8e4585] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {metadata.unreadCount > 9 ? '9+' : metadata.unreadCount}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span
            className={`text-sm truncate ${hasUnread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}
          >
            {otherUser.displayName}
          </span>
          <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
            {conversation.lastMessageAt && formatTimestamp(conversation.lastMessageAt)}
          </span>
        </div>
        <p
          className={`text-xs truncate ${hasUnread ? 'text-slate-700 font-medium' : 'text-slate-500'}`}
        >
          {conversation.lastMessagePreview || 'No messages'}
        </p>
      </div>
    </button>
  );
}
